<template>
  <div>
    <div v-show="exactRoute" class="po-full-screen-map">
      <wo-maps-pane mod-open>
        <wo-maps-pane-header mod-bordered>
          <form autocomplete="off">
            <wo-link v-if="allowCompanySwitch" mod-icon mod-icon-after icon="chevron-down" @click.native.prevent="changeCompany">{{ company.name }}</wo-link>
            <wo-title tag-name="h1" tag-style="h5" v-t="titleKey"></wo-title>

            <po-input-field v-model="searchText" placeholder-key="general.searchTextPlaceholder" :debounce="true" v-focus="true" />
            <wo-spacer mod-md />
          </form>
          <template v-if="resultMessage" slot="description">
            <div>{{ resultMessage }}</div>
            <div v-if="hasOverflow" v-wo-color.light v-wo-text.xs v-t="'salespointMap.overflow'"></div>
          </template>
        </wo-maps-pane-header>
        <wo-maps-pane-scroll>
          <wo-maps-pane-list v-if="hasSalesPoints">
            <wo-maps-pane-list-item v-for="(item, index) in items" :key="item.id" :mod-highlight="item === selectedItem">
              <div @click="select(item, true)">
                <wo-sales-point :title="item.value.company.name" :address="item.value.company.address && item.value.company.address.shortName">
                  <wo-marker slot="media" :class="{'wo-marker--hidden': !item.marker}">{{ index + 1 }}</wo-marker>
                  <wo-meta-data-list>
                    <wo-meta-data mod-sm mod-listed mod-icon icon="drop"><po-display :value="item.value.channel" /></wo-meta-data>
                  </wo-meta-data-list>
                </wo-sales-point>
              </div>
            </wo-maps-pane-list-item>
          </wo-maps-pane-list>
        </wo-maps-pane-scroll>
      </wo-maps-pane>
      <div :id="elementId" class="po-full-screen-map__map"></div>
    </div>
    <router-view v-if="!exactRoute" />
  </div>
</template>

<script>

import _ from 'lodash'
import { mapState } from 'vuex'
import { showModal } from '@/modal'

import { SALES_POINT, SALES_POINT_MAP } from '@/routes/sales-point'

import mapBox from '@/mixins/map-box'
import filtered from '@/mixins/filtered'

import { Permissions } from '@/model/identity'

import { companySalesPointRepository } from '@/repository/sales-point'

import { Geo, MapMarker } from '@/components/geography/util'
import CompanySelectModal from '@/components/admin/CompanySelectModal'

export default {
  mixins: [ mapBox, filtered ],
  props: {
    titleKey: { type: String, default: 'salespointMap.searchTitle' },
    baseFilter: { type: Object },
    salesPointRoute: { type: String, default: SALES_POINT }
  },
  data () {
    return {
      searchText: undefined,
      items: [],
      selectedItem: undefined
    }
  },
  computed: mapState({
    user: state => state.identity.currentUser,
    company: state => state.company.company,
    salesPoints: state => state.salesPoint.companySalesPointList,
    allowCompanySwitch () {
      return this.company && this.user.hasPermission(Permissions.CRUD_SALES_POINT)
    },
    hasSalesPoints () {
      return this.salesPoints && !_.isEmpty(this.salesPoints.items)
    },
    hasOverflow () {
      return this.hasSalesPoints && this.salesPoints.total_count > this.salesPoints.count
    },
    resultMessage () {
      if (this.salesPoints) {
        const key = this.hasOverflow ? 'salespointMap.firstMatches' : 'salespointMap.allMatches'
        return this.$tc(key, this.salesPoints.count, { totalCount: this.salesPoints.total_count })
      }
    },
    filter () {
      return this.company && Object.assign({
        size: 50,
        search_text: this.searchText
      }, this.baseFilter)
    },
    exactRoute () {
      return _.size(this.$route.matched) === 2
    }
  }),
  watch: {
    salesPoints () {
      this.createItems()
    }
  },
  methods: {
    search ({ detail }) {
      this.doFilter().then(detail.resolve)
    },
    applyFilter (filter, pristine) {
      this.selectedItem = undefined
      return this.$store.dispatch(companySalesPointRepository.findAll, { companyId: this.company.id, filter: filter, replace: !pristine })
    },
    onReady () {
      if (this.exactRoute) {
        this.map = this.createMap({ zoom: 15 })
        this.map.addControl(new this.mapBox.NavigationControl())
        this.createItems()
      }
    },
    select (item, manual) {
      if (manual && this.selectedItem === item) {
        this.$router.push(item.route)
      } else {
        this.selectedItem = item
        const coordinates = this.getCoordinates(item.value)
        if (coordinates && this.map) {
          this.map.flyTo({ center: Geo.toLngLat(coordinates) })
          _.each(this.items, i => MapMarker.setPopupState(i.marker, i === item))
        }
      }
    },
    getCoordinates (item) {
      return item.company.address && item.company.address.coordinates
    },
    createItems () {
      _.each(this.items, i => i.marker && i.marker.remove())
      if (this.salesPoints) {
        this.items = _.map(this.salesPoints.items, (item, index) => {
          const wrapped = {
            value: item,
            route: { name: this.salesPointRoute, params: { salesPointId: item.id, companyId: this.company.id } }
          }
          wrapped.marker = this.map && this.createMarker(wrapped, index)
          return wrapped
        })
        if (this.map) {
          const bounds = Geo.toLngLatBounds(_.filter(_.map(this.items, i => this.getCoordinates(i.value))))
          bounds && this.map.fitBounds(bounds, { padding: 100, linear: true, maxZoom: 13 })
        }
      }
    },
    createMarker (item, index) {
      const coordinates = this.getCoordinates(item.value)
      if (coordinates) {
        const marker = new this.mapBox.Marker(MapMarker.options(index + 1))
          .setLngLat(Geo.toLngLat(coordinates))
          .addTo(this.map)

        const popup = new this.mapBox.Popup(MapMarker.popupOptions())
          .setHTML(MapMarker.popupHTML(item.value.company.name, item.value.company.address.shortName))
        popup.on('open', () => {
          this.select(item)
        })
        marker.setPopup(popup)
        return marker
      }
    },
    changeCompany () {
      showModal(CompanySelectModal, { customersOnly: true })
        .then(company => this.$router.push({ name: SALES_POINT_MAP, params: { companyId: company.id } }))
        .catch(e => {})
    }
  }
}
</script>
<style lang="scss">

$header-offset: 80px;
$po-map-pane-width: 480px;

.po-full-screen-map {
  position: absolute;
  top: $header-offset;
  left: 0;
  right: 0;
  bottom: 0;

  @media screen and (min-width: $po-map-pane-width) {
    padding-left: $po-map-pane-width;
  }

  // TODO: until open/closed state is exposed, hide the toggle to prevent gap
  .wo-maps__pane__toggle {
    display: none;
  }

  .wo-marker--hidden {
    visibility: hidden;
  }
}

.po-full-screen-map__map {
  height: 100%;
}
</style>
